import React, { useState } from "react";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardFooter,
} from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { FaTrash } from "react-icons/fa";
import { UsersIcon } from "lucide-react";
import { format } from "date-fns";
import { useNavigate } from "react-router-dom";
import { ClipLoader } from "react-spinners";
import { deleteChatbot } from "../../services/api/ManagerService";
import { useChatbotCountState } from "../../states/useStates";
import toast from "react-hot-toast";

export default function ChatbotCard({
  item,
  onDelete,
  onUserView,
  inlineConfirmId,
  setInlineConfirmId,
}) {
  const navigate = useNavigate();
  const [deleting, setDeleting] = useState(false);
  const [chatbotCount, setChatbotCount] = useChatbotCountState();

  const handleNavigate = () => {
    navigate(`/knowledgebase/${item.chatbot_name}`, {
      state: { config: item },
    });
  };

  const handleDelete = async () => {
    setDeleting(true);
    try {
      await deleteChatbot(item.chatbot_name);
      setChatbotCount(chatbotCount - 1);
      onDelete(item);
      toast.success("Chatbot deleted successfully.");
    } catch (error) {
      toast.error("Failed to delete chatbot.");
    } finally {
      setDeleting(false);
    }
  };

  const isInlineConfirmVisible = inlineConfirmId === item.id;

  return (
    <Card
      onClick={(e) => {
        if (
          e.target.closest("button") ||
          e.target.closest("svg") ||
          e.target.closest("path")
        )
          return;
        handleNavigate();
      }}
      className="w-full max-w-md transition-transform duration-300 ease-in-out transform hover:scale-105 hover:shadow-xl border border-gray-200 rounded-xl"
    >
      <CardHeader className="cursor-pointer">
        <div className="flex items-center justify-between">
          <CardTitle className="text-xl font-semibold">
            {item.chatbot_name}
          </CardTitle>
          <Badge className={"bg-primary text-white"}>
            {item.active ? "Active" : "Inactive"}
          </Badge>
        </div>
        <p className="text-sm text-muted-foreground mt-1">
          {item.chatbot_description.slice(0, 200)}
          {item.chatbot_description.length > 200 ? "..." : ""}
        </p>
      </CardHeader>

      <CardContent className="text-sm space-y-2 cursor-pointer">
        <div>
          <span className="font-medium">👤 Author:</span> {item.user_fullname}
        </div>
        <div>
          <span className="font-medium">📄 Documents:</span>{" "}
          {item.documents_count}
        </div>
        <div>
          <span className="font-medium">📅 Created at:</span>{" "}
          {format(new Date(item.time_created), "dd/MM/yyyy")}
        </div>
      </CardContent>

      <CardFooter className="flex justify-between mt-4 gap-2 cursor-pointer min-h-[60px]">
        {!isInlineConfirmVisible ? (
          <>
            <Button variant="outline" onClick={() => onUserView(item)}>
              <UsersIcon className="mr-2 h-4 w-4" /> Manage Users
            </Button>
            {!deleting ? (
              <Button
                variant="outline"
                className="border-red-500 text-red-500 hover:bg-red-500 hover:text-white"
                onClick={() => setInlineConfirmId(item.id)}
              >
                <FaTrash className="mr-2 h-4 w-4" /> Delete
              </Button>
            ) : (
              <Button
                variant="outline"
                className="border-red-500 text-red-500"
                disabled
              >
                <ClipLoader size={16} color="red" />
              </Button>
            )}
          </>
        ) : (
          <div className="flex w-full items-center justify-between">
            <p className="text-sm text-red-600">
              Are you sure you want to delete the knowledge base?
            </p>
            <div className="flex gap-2">
              <Button
                variant="outline"
                className="border-gray-400 text-gray-600"
                onClick={() => setInlineConfirmId(null)}
              >
                Cancel
              </Button>
              <Button
                variant="destructive"
                onClick={async () => {
                  await handleDelete();
                  setInlineConfirmId(null);
                }}
              >
                Delete
              </Button>
            </div>
          </div>
        )}
      </CardFooter>
    </Card>
  );
}
